import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { DNABox, DNAButton, DNAText, util } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { CommonIcon } from 'src/components/DNA';
import {
  MATCH_SLIDE_STATUS,
  usePublisherVersioningState,
} from 'src/state/machines/publisherVersioning/PublisherVersioningProvider';
import {
  SLIDE_GROUP_TARGET_CONTAINER_WIDTH_LARGE,
  SLIDE_GROUP_TARGET_CONTAINER_WIDTH_SMALL,
  useMatchSlidesState,
} from 'src/state/machines/publisherVersioning/MatchSlides/MatchSlidesProvider';
import { useSingleItemDnd } from 'src/components/DnD/DNASingleItemDnd';

const S = StyleSheet.create({
  recommendedConfirmationContainer: {
    backgroundColor: colors['color-warning-5'],
    borderColor: colors['color-warning-500'],
    borderWidth: 1,
    borderRadius: 4,
    paddingVertical: 8,
    paddingHorizontal: 16,
    minHeight: 45,
    marginBottom: 16,
  },
})

interface RecommendationConfirmationProps {
  pageId: string
}

const RecommendedConfirmation: React.FC<RecommendationConfirmationProps> = ({ pageId }) => {
  const { groupedTargetItems } = useSingleItemDnd();
  const { onMatch, onUnmatch } = useMatchSlidesState();
  const { matchSlideStates } = usePublisherVersioningState();
  const isLargeScreen = useMediaQuery({
    query: '(min-width: 1300px)',
  })

  const mappedItem = useMemo(() => groupedTargetItems[pageId]?.[0]?.itemId || null, [groupedTargetItems, pageId])

  const showConfirmationMessage = matchSlideStates[pageId] &&
    matchSlideStates[pageId].status === MATCH_SLIDE_STATUS.NEEDS_REVIEW &&
    !matchSlideStates[pageId]?.duplicateSlides?.length;

  if (!showConfirmationMessage || !mappedItem) return null
  return (
    <DNABox
      alignX="start"
      alignY="center"
      spacing="sm"
      fill
      style={util.mergeStyles(
        undefined,
        S.recommendedConfirmationContainer,
        { width: isLargeScreen ? SLIDE_GROUP_TARGET_CONTAINER_WIDTH_LARGE : SLIDE_GROUP_TARGET_CONTAINER_WIDTH_SMALL },
      )}
    >
      <CommonIcon variant="warning-circle" />
      <DNAText status="warning">
        Do these slides match?
      </DNAText>
      <DNAButton
        appearance="outline"
        status="warning"
        onPress={() => onMatch(groupedTargetItems, pageId, mappedItem)}
      >
        Yes
      </DNAButton>
      <DNAButton
        appearance="outline"
        status="warning"
        onPress={() => onUnmatch(pageId)}
      >
        No
      </DNAButton>
    </DNABox>
  )
}

export default RecommendedConfirmation
